import { FETCH_WELLNESS_GOAL } from 'actions/wellnessGoals';
import { call, put, takeEvery } from 'redux-saga/effects';
import DateFormatter from 'utils/date/DateFormatter';
import DateParser from 'utils/date/DateParser';
import * as UrlUtil from 'utils/UrlUtil';

import {
    FETCH_WEIGHT_LIST,
    FETCH_WEIGHT_LIST_FETCHING,
    FETCH_WEIGHT_LIST_SUCCESS,
    FETCH_WEIGHT_LIST_FAIL,
    ADD_WEIGHT_REQUEST,
    DELETE_WEIGHT_REQUEST
} from '../actions/ActionTypes';
import {
    ADD_USER_WEIGHT,
    ADD_USER_WEIGHT_FAIL,
    ADD_USER_WEIGHT_START,
    ADD_USER_WEIGHT_SUCCESS,
    DELETE_USER_WEIGHT,
    DELETE_USER_WEIGHT_FAIL,
    DELETE_USER_WEIGHT_FETCHING,
    DELETE_USER_WEIGHT_SUCCESS,
    FETCH_52_WEEKS_WEIGHTS_REPORT_FAIL,
    FETCH_52_WEEKS_WEIGHTS_REPORT_FETCHING,
    FETCH_52_WEEKS_WEIGHTS_REPORT_SUCCESS,
    FETCH_52_WEEKS_WEIGHTS,
    FETCH_52_WEEKS_WEIGHTS_FAIL,
    FETCH_52_WEEKS_WEIGHTS_FETCHING,
    FETCH_52_WEEKS_WEIGHTS_SUCCESS,
    FETCH_DAY_WEIGHTS,
    FETCH_DAY_WEIGHTS_FAIL,
    FETCH_DAY_WEIGHTS_FETCHING,
    FETCH_DAY_WEIGHTS_SUCCESS,
    FETCH_FIRST_WEIGHT,
    FETCH_FIRST_WEIGHT_SUCCESS,
    FETCH_LATEST_WEIGHT,
    FETCH_LATEST_WEIGHT_FAIL,
    FETCH_LATEST_WEIGHT_FETCHING,
    FETCH_LATEST_WEIGHT_SUCCESS,
    FETCH_MONTHLY_WEIGHTS,
    FETCH_MONTHLY_WEIGHTS_FAIL,
    FETCH_MONTHLY_WEIGHTS_FETCHING,
    FETCH_MONTHLY_WEIGHTS_SUCCESS,
    FETCH_MULTIPLE_DAYS_WEIGHTS,
    FETCH_MULTIPLE_DAYS_WEIGHTS_FAIL,
    FETCH_MULTIPLE_DAYS_WEIGHTS_FETCHING,
    FETCH_MULTIPLE_DAYS_WEIGHTS_SUCCESS,
    UPDATE_USER_GOAL,
    UPDATE_USER_GOAL_FAIL,
    UPDATE_USER_GOAL_START,
    UPDATE_USER_GOAL_SUCCESS,
    FETCH_MULTIPLE_DAILY_WEIGHTS,
    FETCH_MULTIPLE_DAILY_WEIGHTS_FETCHING,
    FETCH_MULTIPLE_DAILY_WEIGHTS_SUCCESS,
    FETCH_MULTIPLE_DAILY_WEIGHTS_FAIL,
    FETCH_52_WEEKS_WEIGHTS_REPORT,
    FETCH_WEIGHT_GAIN_GOALS,
    FETCH_WEIGHT_GAIN_GOALS_FETCHING,
    FETCH_WEIGHT_GAIN_GOALS_SUCCESS,
    FETCH_WEIGHT_GAIN_GOALS_FAIL,
    DELETE_USER_WEIGHT_GOAL,
    DELETE_USER_WEIGHT_GOAL_FAIL,
    DELETE_USER_WEIGHT_GOAL_SUCCESS,
    DELETING_USER_WEIGHT_GOAL
} from 'actions/weightActions';
import { trackingPeriodType, userGoalType } from '../constants';
import { gcApi } from '../config/axios';
import { handlingErrorCallback, handlingSuccessCallback } from './sagasUtils';
import { onAddWeight, onDeleteWeight } from '../actions';

export function* fetchWeightListSaga(action) {
    try {
        yield put({
            type: FETCH_WEIGHT_LIST_FETCHING
        });

        const response = yield call(
            gcApi.get,
            `/weight-service/weight/daterangesnapshot?startDate=${action.startDate}&endDate=${action.endDate}`
        );

        yield put({
            type: FETCH_WEIGHT_LIST_SUCCESS,
            weightList: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_WEIGHT_LIST_FAIL,
            weightList: {}
        });
    }
}

export function* fetchDayWeightsSaga(action) {
    try {
        yield put({
            type: FETCH_DAY_WEIGHTS_FETCHING
        });

        const response = yield call(gcApi.get, `/weight-service/weight/dayview/${action.date}`);

        yield put({
            type: FETCH_DAY_WEIGHTS_SUCCESS,
            date: action.date,
            data: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_DAY_WEIGHTS_FAIL,
            data: {}
        });
    }
}

export function* fetchFirstWeightSaga() {
    try {
        const response = yield call(gcApi.get, '/weight-service/weight/first');

        yield put({
            type: FETCH_FIRST_WEIGHT_SUCCESS,
            data: response.data
        });
    } catch (e) {}
}

export function* fetchLatestWeightSaga(action) {
    try {
        yield put({
            type: FETCH_LATEST_WEIGHT_FETCHING
        });

        const response = yield call(gcApi.get, `/weight-service/weight/latest?date=${action.date}&ignorePriority=true`);

        yield put({
            type: FETCH_LATEST_WEIGHT_SUCCESS,
            date: action.date,
            data: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_LATEST_WEIGHT_FAIL,
            data: {}
        });
    }
}

export function* updateUserWeightGoal(action) {
    try {
        yield put({
            type: UPDATE_USER_GOAL_START
        });

        let url = `/goal-service/goal/user/effective/WEIGHT_GRAMS/${action.payload.startDate}`;

        const response = yield call(gcApi.put, url, {
            goalValue: action.payload.goalValue,
            goalType: 'WEIGHT_GRAMS'
        });

        //after updating the goal fetch the new values
        yield put({
            type: FETCH_WELLNESS_GOAL,
            trackingPeriodType: action.payload.trackingPeriodType,
            userGoalType: action.payload.userGoalType
        });

        yield put({
            type: UPDATE_USER_GOAL_SUCCESS,
            response
        });
    } catch (e) {
        yield put({
            type: UPDATE_USER_GOAL_FAIL,
            data: {}
        });
    }
}

export function* addUserWeight(action) {
    try {
        yield put({
            type: ADD_USER_WEIGHT_START
        });

        let url = `/weight-service/user-weight`;
        const response = yield call(gcApi.post, url, {
            ...action.payload
        });

        //after updateing the weights fetch the new values
        if (action.actionToDispatch && action.actionToDispatch.type) {
            yield put(action.actionToDispatch);
        }

        yield put({
            type: ADD_USER_WEIGHT_SUCCESS,
            response,
            reloadPage: action.reloadPage
        });
    } catch (e) {
        yield put({
            type: ADD_USER_WEIGHT_FAIL,
            data: {}
        });
    }
}

export function* fetchMonthlyWeights(action) {
    try {
        yield put({
            type: FETCH_MONTHLY_WEIGHTS_FETCHING
        });

        const response = yield call(
            gcApi.get,
            `/weight-service/weight/monthRange/${action.dates.startDate}/${action.dates.endDate}`
        );

        yield put({
            type: FETCH_MONTHLY_WEIGHTS_SUCCESS,
            endDate: action.dates.endDate,
            data: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_MONTHLY_WEIGHTS_FAIL,
            data: {}
        });
    }
}

export function* fetch52WeeksWeights(action) {
    try {
        yield put({
            type: FETCH_52_WEEKS_WEIGHTS_FETCHING
        });

        const response = yield call(
            gcApi.get,
            `/weight-service/weight/weeklyRange/${action.dates.startDate}/${action.dates.endDate}`
        );

        yield put({
            type: FETCH_52_WEEKS_WEIGHTS_SUCCESS,
            endDate: action.dates.endDate,
            data: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_52_WEEKS_WEIGHTS_FAIL,
            data: {}
        });
    }
}

export function* fetch52WeeksWeightsReport(action) {
    try {
        yield put({
            type: FETCH_52_WEEKS_WEIGHTS_REPORT_FETCHING
        });

        const queryParams = UrlUtil.toQueryString({
            includeAll: true
        });

        const response = yield call(
            gcApi.get,
            `/weight-service/weight/range/${action.dates.startDate}/${action.dates.endDate}${queryParams}`
        );

        yield put({
            type: FETCH_52_WEEKS_WEIGHTS_REPORT_SUCCESS,
            endDate: action.dates.endDate,
            data: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_52_WEEKS_WEIGHTS_REPORT_FAIL,
            data: {}
        });
    }
}

export function* fetchMultipleDaysWeights(action) {
    try {
        yield put({
            type: FETCH_MULTIPLE_DAYS_WEIGHTS_FETCHING,
            dateRange: action.dateRange
        });

        const queryParams = UrlUtil.toQueryString({
            includeAll: true
        });

        const response = yield call(
            gcApi.get,
            `/weight-service/weight/range/${action.dates.startDate}/${action.dates.endDate}${queryParams}`
        );

        yield put({
            type: FETCH_MULTIPLE_DAYS_WEIGHTS_SUCCESS,
            endDate: action.dates.endDate,
            data: response.data,
            dateRange: action.dateRange
        });
    } catch (e) {
        yield put({
            type: FETCH_MULTIPLE_DAYS_WEIGHTS_FAIL,
            data: {},
            dateRange: action.dateRange
        });
    }
}

export function* deleteUserWeight(action) {
    try {
        yield put({
            type: DELETE_USER_WEIGHT_FETCHING
        });
        let url = `weight-service/weight/${action.date}/byversion/${action.weightId}`;
        const response = yield call(gcApi.delete, url);

        if (action.actionToDispatch && action.actionToDispatch.type) {
            yield put(action.actionToDispatch);
        }

        yield put({
            type: DELETE_USER_WEIGHT_SUCCESS,
            weightId: action.weightId,
            response,
            reloadPage: action.reloadPage
        });
    } catch (e) {
        yield put({
            type: DELETE_USER_WEIGHT_FAIL
        });
    }
}

export function* fetchMultipleDailyWeights(action) {
    const callback = action.callback ? action.callback : null;

    try {
        yield put({
            type: FETCH_MULTIPLE_DAILY_WEIGHTS_FETCHING
        });

        const response = yield call(
            gcApi.get,
            `/weight-service/weight/daily/last?startDate=${action.dates.startDate}&endDate=${action.dates.endDate}`
        );

        yield put({
            type: FETCH_MULTIPLE_DAILY_WEIGHTS_SUCCESS,
            data: response.data
        });

        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield put({
            type: FETCH_MULTIPLE_DAILY_WEIGHTS_FAIL,
            data: null
        });
    }
}

export function* fetchWeightGainGoals(action) {
    try {
        yield put({
            type: FETCH_WEIGHT_GAIN_GOALS_FETCHING
        });

        const response = yield call(
            gcApi.get,
            `/goal-service/goal/user/effective/weightgoal/${action.dates.startDate}/${action.dates.endDate}`
        );

        yield put({
            type: FETCH_WEIGHT_GAIN_GOALS_SUCCESS,
            data: response.data
        });
    } catch (e) {
        yield put({
            type: FETCH_WEIGHT_GAIN_GOALS_FAIL,
            data: null
        });
    }
}

export function* deleteUserWeightGoal(action) {
    try {
        yield put({
            type: DELETING_USER_WEIGHT_GOAL
        });
        let url = `/goal-service/goal/user/${action.goalId}`;

        let response = yield call(gcApi.delete, url);

        //after updating the goal fetch the new values
        yield put({
            type: FETCH_WELLNESS_GOAL,
            trackingPeriodType: trackingPeriodType.daily,
            userGoalType: userGoalType.weight
        });

        yield put({
            type: DELETE_USER_WEIGHT_GOAL_SUCCESS
        });
    } catch (e) {
        yield put({
            type: DELETE_USER_WEIGHT_GOAL_FAIL
        });
    }
}

export function* addWeightSaga({ weight, unit, date, callback }) {
    try {
        yield put(onAddWeight());
        const response = yield gcApi.post(`/weight-service/user-weight`, {
            date,
            dateTimestamp: DateFormatter.formatConnectTimestamp(DateParser.parseISO(date)),
            gmtTimestamp: DateFormatter.formatConnectTimestampUTC(DateParser.parseISO(date)),
            unitKey: unit,
            value: weight
        });

        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export function* deleteWeightSaga({ date, callback }) {
    try {
        yield put(onDeleteWeight());
        const response = yield gcApi.delete(`/biometric-service/biometric/${date}`);
        yield call(handlingSuccessCallback, callback, response);
    } catch (e) {
        yield call(handlingErrorCallback, callback, e);
    }
}

export const weightSagas = [
    takeEvery(FETCH_WEIGHT_LIST, fetchWeightListSaga),
    takeEvery(FETCH_DAY_WEIGHTS, fetchDayWeightsSaga),
    takeEvery(FETCH_FIRST_WEIGHT, fetchFirstWeightSaga),
    takeEvery(FETCH_LATEST_WEIGHT, fetchLatestWeightSaga),
    takeEvery(DELETE_USER_WEIGHT_GOAL, deleteUserWeightGoal),
    takeEvery(UPDATE_USER_GOAL, updateUserWeightGoal),
    takeEvery(ADD_USER_WEIGHT, addUserWeight),
    takeEvery(FETCH_MONTHLY_WEIGHTS, fetchMonthlyWeights),
    takeEvery(FETCH_52_WEEKS_WEIGHTS, fetch52WeeksWeights),
    takeEvery(FETCH_52_WEEKS_WEIGHTS_REPORT, fetch52WeeksWeightsReport),
    takeEvery(FETCH_MULTIPLE_DAYS_WEIGHTS, fetchMultipleDaysWeights),
    takeEvery(DELETE_USER_WEIGHT, deleteUserWeight),
    takeEvery(FETCH_MULTIPLE_DAILY_WEIGHTS, fetchMultipleDailyWeights),
    takeEvery(FETCH_WEIGHT_GAIN_GOALS, fetchWeightGainGoals),
    takeEvery(ADD_WEIGHT_REQUEST, addWeightSaga),
    takeEvery(DELETE_WEIGHT_REQUEST, deleteWeightSaga)
];
